import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Edit, Add, Delete, PhotoCamera, LocalDining, QueryBuilder, Person4, DateRange } from "@mui/icons-material";
import MicrowaveIcon from "@mui/icons-material/Microwave";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import apiClient from "../utils/apiClient";
import AuthContext from "../auth/AuthContext";
import RecipeForm from "../components/RecipeForm";
import RecipeStepEditModal from "../components/RecipeStepEditModal";
import { getRecipeUrl, getCreatorUsername } from "../utils/recipeUtils";
import { parseTotalTimeFromSteps } from "../utils/timeParser";

const RecipeDetail = () => {
  const { recipe_uuid, creator, name } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recipeFormOpen, setRecipeFormOpen] = useState(false);
  const [stepEditModalOpen, setStepEditModalOpen] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [creatingVariant, setCreatingVariant] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  // UUID pattern check
  const isUUID = (str) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  const fetchRecipe = async () => {
    try {
      let response;
      if (recipe_uuid && !creator && !name) {
        // Fetch by UUID (when recipe_uuid param exists and no creator/name)
        response = await apiClient.get(`/recipes/${recipe_uuid}`);
      } else if (creator && name) {
        // Fetch by creator and name - FastAPI automatically handles URL decoding
        response = await apiClient.get(`/recipes/${encodeURIComponent(creator)}/${encodeURIComponent(name)}`);
      } else {
        throw new Error("Invalid route parameters");
      }
      if (response && response.data) {
        setRecipe(response.data);
      } else {
        console.error("Failed to fetch recipe: No data in response");
        // Don't set recipe to null if we already have one - might be a temporary network issue
        if (!recipe) {
          setRecipe(null);
        }
      }
    } catch (err) {
      console.error("Failed to fetch recipe:", err);
      // Only set recipe to null if we don't have one already
      // This prevents clearing the recipe on temporary network errors
      if (!recipe) {
        setRecipe(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipe();
  }, [recipe_uuid, creator, name]);

  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours} hour${hours > 1 ? "s" : ""}`;
    }
    return `${hours} hour${hours > 1 ? "s" : ""} ${mins} minute${mins > 1 ? "s" : ""}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Get flat list of steps sorted by step_number
  const getSortedSteps = (steps) => {
    if (!steps || steps.length === 0) return [];
    return [...steps].sort((a, b) => a.step_number - b.step_number);
  };

  // Calculate total time from step markdown text
  const calculateTotalTimeFromSteps = () => {
    if (!recipe || !recipe.steps) return 0;
    const stepTexts = recipe.steps.map(step => step.step_text || '');
    return parseTotalTimeFromSteps(stepTexts);
  };

  const calculatedTime = calculateTotalTimeFromSteps();

  const handleEditStep = (step) => {
    setEditingStep(step);
    setStepEditModalOpen(true);
  };

  const handleDeleteStep = async (stepUuid) => {
    if (!recipe) return;
    try {
      await apiClient.delete(`/recipes/${recipe.recipe_uuid}/steps/${stepUuid}`);
      // Refresh recipe data
      await fetchRecipe();
    } catch (err) {
      console.error("Failed to delete step:", err);
      alert("Failed to delete step. Please try again.");
    }
  };

  const handleSaveStep = async (stepUuid, updates) => {
    try {
      if (stepUuid) {
        // Editing existing step - use PUT endpoint
        const updatePayload = {
          step_text: updates.step_text,
        };
        if (updates.step_number !== undefined) {
          updatePayload.step_number = updates.step_number;
        }
        await apiClient.put(`/recipes/${recipe.recipe_uuid}/steps/${stepUuid}`, updatePayload);
      } else {
        // Adding new step - use POST endpoint
        const maxStepNumber = recipe.steps.length > 0 
          ? Math.max(...recipe.steps.map(s => s.step_number || 0))
          : 0;
        
        const createPayload = {
          step_number: updates.step_number !== undefined ? updates.step_number : maxStepNumber + 1,
          step_text: updates.step_text,
        };
        
        await apiClient.post(`/recipes/${recipe.recipe_uuid}/steps`, createPayload);
      }

      // Refresh recipe data
      await fetchRecipe();
      
      // Close modal after saving (for both adding and editing)
      setStepEditModalOpen(false);
      setEditingStep(null);
    } catch (err) {
      console.error("Failed to save step:", err);
      alert(`Failed to ${stepUuid ? 'update' : 'add'} step. Please try again.`);
      setStepEditModalOpen(false);
      setEditingStep(null);
    }
  };

  const handleAddStep = () => {
    // Open modal to add a new step
    setEditingStep(null);
    setStepEditModalOpen(true);
  };

  const handleEditRecipe = () => {
    setCreatingVariant(false);
    setRecipeFormOpen(true);
  };

  const handleRecipeDelete = async (deletedRecipeUuid) => {
    if (recipe && deletedRecipeUuid === recipe.recipe_uuid) {
      // If we deleted the current recipe, navigate back to recipes list
      navigate("/recipes");
    }
  };

  const handleRecipeFormClose = async (success, updatedRecipe = null) => {
    const wasCreatingVariant = creatingVariant;
    setRecipeFormOpen(false);
    setCreatingVariant(false);
    if (success) {
      if (wasCreatingVariant && updatedRecipe) {
        // If we just created a variant, navigate to the new recipe
        navigate(getRecipeUrl(updatedRecipe));
      } else if (updatedRecipe) {
        // If we got an updated recipe back (with images), use it
        setRecipe(updatedRecipe);
      } else {
        await fetchRecipe();
      }
    }
  };

  const handleCreateVariant = () => {
    setCreatingVariant(true);
    setRecipeFormOpen(true);
  };

  const handleImageDeleted = (imageUuid) => {
    // Update recipe state to remove the deleted image from the images array
    // This updates the gallery immediately without refreshing the entire recipe
    setRecipe(prev => {
      if (!prev || !prev.images) return prev;
      return {
        ...prev,
        images: prev.images.filter(img => img.image_uuid !== imageUuid)
      };
    });
  };


  const handleImageClick = (imageUuid) => {
    const baseUrl = apiClient.defaults.baseURL || "";
    const imageUrl = `${baseUrl}/static/recipe_images/${imageUuid}.jpg`;
    setPreviewImageUrl(imageUrl);
    setImagePreviewOpen(true);
  };

  const renderStepImages = (images) => {
    if (!images || images.length === 0) return null;
    
    return (
      <Box sx={{ float: "right", ml: 2, mb: 2, maxWidth: "300px" }}>
        {images.map((image) => (
          <Box
            key={image.image_uuid}
            component="img"
            src={`${apiClient.defaults.baseURL || ""}/static/recipe_images/${image.image_uuid}_thumb.jpg`}
            alt="Step"
            onClick={() => handleImageClick(image.image_uuid)}
            sx={{
              maxWidth: "100%",
              height: "auto",
              cursor: "pointer",
              borderRadius: 1,
              mb: 1,
              display: "block",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          />
        ))}
      </Box>
    );
  };

  const renderStep = (step, isLastStep) => {
    return (
      <Box 
        key={step.recipe_step_uuid} 
        sx={{ 
          mb: isLastStep ? 0 : 1.5, // Smaller margin between steps, none after last
          position: "relative",
          "&:hover .edit-step-button": {
            opacity: 1,
            visibility: "visible"
          }
        }}
      >
        {isCreator && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
            <IconButton
              className="edit-step-button"
              size="small"
              onClick={() => handleEditStep(step)}
              sx={{ 
                opacity: 0,
                visibility: "hidden",
                transition: "opacity 0.2s, visibility 0.2s",
                "&:hover": { opacity: 1 } 
              }}
              title="Edit step"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
        )}
        
        {/* Render markdown content */}
        <Box 
          sx={{ 
            mt: 0.5,
            "& h1": {
              fontSize: "1.3rem",
              fontWeight: 600,
              fontFamily: 'var(--font-decorative)',
              color: 'var(--color-sepia)',
              mt: 2,
              mb: 1,
            },
            "& h2": {
              fontSize: "1.15rem",
              fontWeight: 600,
              fontFamily: 'var(--font-decorative)',
              color: 'var(--color-sepia)',
              mt: 2,
              mb: 1,
            },
            "& h3": {
              fontSize: "1rem",
              fontWeight: 600,
              fontFamily: 'var(--font-decorative)',
              color: 'var(--color-sepia)',
              mt: 1.5,
              mb: 0.5,
            },
            "& img": {
              maxWidth: "400px",
              width: "auto",
              height: "auto",
              float: "right",
              marginLeft: 2,
              marginBottom: 1,
              marginTop: 1,
              borderRadius: 1,
              display: "block",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              },
            },
            "& p": {
              textAlign: "justify",
            },
            // Don't clear floats after each step - let them flow together
          }}
        >
          <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            components={{
              img: ({ node, ...props }) => {
                const handleImageClick = () => {
                  // Get full-size image URL (remove _thumb if present)
                  let fullSizeUrl = props.src || '';
                  if (fullSizeUrl.includes('_thumb')) {
                    fullSizeUrl = fullSizeUrl.replace('_thumb', '');
                  }
                  setPreviewImageUrl(fullSizeUrl);
                  setImagePreviewOpen(true);
                };
                return (
                  <img
                    {...props}
                    onClick={handleImageClick}
                    style={{ cursor: 'pointer' }}
                    alt={props.alt || 'Recipe image'}
                  />
                );
              },
              time: ({ children, ...props }) => {
                // Render <time> tags as bold text
                return <strong {...props}>{children}</strong>;
              },
              span: ({ node, ...props }) => {
                // Render spans with data-oven attribute as MicrowaveIcon
                if (props['data-oven'] === 'true' || props['data-oven'] === true) {
                  return <MicrowaveIcon sx={{ fontSize: '1.2rem', verticalAlign: 'middle', mx: 0.5, display: 'inline-block' }} />;
                }
                // Default span rendering
                return <span {...props} />;
              },
            }}
          >
            {(() => {
              // Preprocess: convert <time> tags to markdown bold and <uses_oven> to span with data attribute
              let processedText = step.step_text || "";
              // Replace <time>content</time> with **content** for bold rendering
              processedText = processedText.replace(/<time>([^<]+)<\/time>/gi, '**$1**');
              // Convert <uses_oven> to <span data-oven> for proper HTML parsing
              processedText = processedText.replace(/<uses_oven>/gi, '<span data-oven="true"></span>');
              return processedText;
            })()}
          </ReactMarkdown>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!recipe) {
    return <Typography>Recipe not found</Typography>;
  }

  const isCreator = user && recipe.created_by === user.user_uuid;
  const sortedSteps = getSortedSteps(recipe.steps);

  // Find header image - only use explicitly marked header image, no fallback
  const headerImage = recipe.images?.find(img => img.is_header_image);
  const recipeImageUrl = headerImage 
    ? `${apiClient.defaults.baseURL || ""}/static/recipe_images/${headerImage.image_uuid}.jpg`
    : null;
  
  // Gallery images: all images except header image
  const galleryImages = recipe.images?.filter(img => !img.is_header_image) || [];

  return (
    <Box sx={{ padding: 3 }}>
      {/* Top buttons row */}
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          mb: 3,
          position: "relative",
          "&:hover .recipe-action-buttons": {
            opacity: 1,
            visibility: "visible"
          }
        }}
      >
        <Button
          component={Link}
          to="/recipes"
          variant="outlined"
          className="vintage-button"
        >
          ⟸ Back to Recipes
        </Button>
        <Box 
          className="recipe-action-buttons"
          sx={{ 
            display: "flex", 
            gap: 1,
            opacity: 0,
            visibility: "hidden",
            transition: "opacity 0.2s, visibility 0.2s"
          }}
        >
          {isCreator && (
            <Button
              variant="outlined"
              onClick={handleEditRecipe}
              className="vintage-button"
            >
              Edit Recipe
            </Button>
          )}
          {user && !recipe.parent_recipe_uuid && (
            <Button
              variant="contained"
              onClick={handleCreateVariant}
              className="vintage-button"
            >
              Create Variant
            </Button>
          )}
        </Box>
      </Box>

      {/* Image with ribbon overlay or just ribbon if no image */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxHeight: recipeImageUrl ? "400px" : "auto",
          overflow: "hidden",
          borderRadius: 2,
          mb: 3,
          backgroundColor: recipeImageUrl ? "transparent" : "rgba(0, 0, 0, 0.05)",
        }}
      >
        {recipeImageUrl && (
          <Box
            component="img"
            src={recipeImageUrl}
            alt={recipe.name}
            className="recipe-image"
            sx={{
              width: "100%",
              height: "auto",
              maxHeight: "400px",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
        {/* Semi-transparent ribbon overlay */}
        <Box
          sx={{
            position: recipeImageUrl ? "absolute" : "relative",
            bottom: recipeImageUrl ? 0 : "auto",
            left: 0,
            right: 0,
            background: recipeImageUrl 
              ? "linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5), transparent)"
              : "rgba(0, 0, 0, 0.05)",
            padding: 3,
            color: recipeImageUrl ? "white" : "text.primary",
          }}
        >
          <Typography variant="h3" className="vintage-script-header" sx={{ mb: 1, color: recipeImageUrl ? "white" : "inherit" }}>
            {recipe.name}
          </Typography>
          {recipe.parent_recipe && (
            <Typography variant="body2" sx={{ mb: 1, color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary", fontStyle: "italic" }}>
              Variant of: {recipe.parent_recipe.name}
            </Typography>
          )}
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1, justifyContent: "center", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LocalDining sx={{ fontSize: "1rem", color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary" }} />
                <Typography variant="body2" sx={{ color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary" }}>
                  {recipe.serves_amount}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <QueryBuilder sx={{ fontSize: "1rem", color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary" }} />
                <Typography variant="body2" sx={{ color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary" }}>
                  {calculatedTime > 0 ? formatTime(calculatedTime) : "N/A"}
                </Typography>
              </Box>
              {recipe.creator && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Person4 sx={{ fontSize: "1rem", color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary" }} />
                  <Typography 
                    component={Link}
                    to={`/recipes/${getCreatorUsername(recipe)}`}
                    variant="body2" 
                    sx={{ 
                      color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary",
                      textDecoration: "none",
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline"
                      }
                    }}
                  >
                    {recipe.creator.username || recipe.creator.email}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <DateRange sx={{ fontSize: "1rem", color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary" }} />
                <Typography variant="body2" sx={{ color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary" }}>
                  {formatDate(recipe.creation_date)}
                </Typography>
              </Box>
            </Box>
            {/* Tags line - show first 10 tags */}
            {recipe.tags && recipe.tags.length > 0 && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1, justifyContent: "center", alignItems: "center" }}>
                {recipe.tags.slice(0, 10).map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag.name || tag}
                    size="small"
                    onClick={() => navigate(`/recipes?tag=${encodeURIComponent(tag.name || tag)}`)}
                    sx={{
                      backgroundColor: recipeImageUrl ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.08)",
                      color: recipeImageUrl ? "rgba(255, 255, 255, 0.9)" : "text.secondary",
                      border: recipeImageUrl ? "1px solid rgba(255, 255, 255, 0.3)" : "1px solid rgba(0, 0, 0, 0.12)",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: recipeImageUrl ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.12)",
                      }
                    }}
                  />
                ))}
              </Box>
            )}
        </Box>
      </Box>

      {/* Ingredients and Description Side by Side */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Ingredients Section - Max 250px width */}
        <Grid item xs={12} sm={4} md={3}>
          <Card 
            sx={{ 
              mb: 0,
              borderRadius: 3,
              border: "2px solid",
              borderColor: "primary.main",
              maxWidth: "250px",
              boxShadow: 4,
              backgroundColor: "background.paper",
              "&:hover": {
                boxShadow: 8,
                transform: "translateY(-2px)",
                transition: "all 0.3s ease",
              },
              transition: "all 0.3s ease",
            }}
          >
            <CardContent>
              <Typography 
                variant="h5" 
                className="rotation-plan-subheader"
                sx={{
                  mb: 2,
                  borderBottom: '2px solid var(--color-border-vintage)',
                  pb: 1,
                }}
              >
                Ingredients
              </Typography>
          {recipe.ingredients && recipe.ingredients.length > 0 ? (
            (() => {
              // Group ingredients by group_name (use empty string for null, but display as "Ingredients")
              const groupedIngredients = {};
              recipe.ingredients.forEach(ing => {
                // Access group_name from the ingredient recipe object
                const groupName = (ing.group_name !== null && ing.group_name !== undefined) ? ing.group_name : "";
                if (!groupedIngredients[groupName]) {
                  groupedIngredients[groupName] = [];
                }
                groupedIngredients[groupName].push(ing);
              });

              // Sort ingredients within each group by order field
              Object.keys(groupedIngredients).forEach(groupName => {
                groupedIngredients[groupName].sort((a, b) => (a.order || 0) - (b.order || 0));
              });

              // Sort groups: null/empty groups first, then alphabetically (matching backend logic)
              const groupNames = Object.keys(groupedIngredients).sort((a, b) => {
                // Empty string (null group_name) comes first
                if (a === "" && b !== "") return -1;
                if (a !== "" && b === "") return 1;
                return a.localeCompare(b);
              });

              return (
                <>
                  {groupNames.map((groupName, groupIndex) => {
                    const groupIngredients = groupedIngredients[groupName];
                    const hasGroupName = groupName && groupName.trim() !== "";
                    return (
                      <Box key={groupName || "default"} sx={{ mb: groupIndex < groupNames.length - 1 ? 3 : 0 }}>
                        {/* Only show group header if group has a name */}
                        {hasGroupName && (
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              mb: 1.5, 
                              mt: groupIndex > 0 ? 2 : 0,
                              fontWeight: 700, 
                              color: "primary.main",
                              textTransform: "uppercase",
                              fontSize: "0.875rem",
                              letterSpacing: "0.5px"
                            }}
                          >
                            {groupName}
                          </Typography>
                        )}
                        <Grid container spacing={2}>
                          {groupIngredients.map((ingredient, index) => (
                            <Grid item xs={12} key={index}>
                              <Box sx={{ py: 1, borderBottom: index < groupIngredients.length - 1 ? "1px solid" : "none", borderColor: "divider" }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {ingredient.ingredient?.name || "Unknown"}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                  {ingredient.amount} {ingredient.unit}
                                  {ingredient.note && ` (${ingredient.note})`}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    );
                  })}
                </>
              );
            })()
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No ingredients listed
            </Typography>
          )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Description and Steps Section */}
        <Grid item xs={12} sm={8} md={9}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {recipe.description && (
              <Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <CardContent>
                  <Typography 
                    variant="h5"
                    className="rotation-plan-subheader"
                    sx={{
                      mb: 2,
                      borderBottom: '2px solid var(--color-border-vintage)',
                      pb: 1,
                    }}
                  >
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {recipe.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Steps Section */}
            <Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
              <CardContent>
                <Typography 
                  variant="h5"
                  className="rotation-plan-subheader"
                  sx={{
                    mb: 2,
                    borderBottom: '2px solid var(--color-border-vintage)',
                    pb: 1,
                  }}
                >
                  Instructions
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    "&:hover .add-step-button": {
                      opacity: 1,
                      visibility: "visible"
                    }
                  }}
                >
                  {sortedSteps.length > 0 ? (
                    <Box
                      sx={{
                        // Clear floats only at the end of all steps
                        "&::after": {
                          content: '""',
                          display: "table",
                          clear: "both",
                        },
                      }}
                    >
                      {sortedSteps.map((step, index) => 
                        renderStep(step, index === sortedSteps.length - 1)
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                      No steps listed
                    </Typography>
                  )}
                  {isCreator && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        className="add-step-button"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleAddStep}
                        sx={{ 
                          opacity: 0,
                          visibility: "hidden",
                          transition: "opacity 0.2s, visibility 0.2s"
                        }}
                      >
                        Add Step
                      </Button>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Gallery Section - Show if there are non-header images */}
      {galleryImages.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Card>
            <CardContent>
              <Typography 
                variant="h5"
                className="rotation-plan-subheader"
                sx={{
                  mb: 2,
                  borderBottom: '2px solid var(--color-border-vintage)',
                  pb: 1,
                }}
              >
                Gallery
              </Typography>
              <Grid container spacing={2}>
                {galleryImages.map((image) => (
                  <Grid item xs={6} sm={4} md={3} key={image.image_uuid}>
                    <Box
                      component="img"
                      src={`${apiClient.defaults.baseURL || ""}/static/recipe_images/${image.image_uuid}_thumb.jpg`}
                      alt="Recipe"
                      onClick={() => handleImageClick(image.image_uuid)}
                      sx={{
                        width: "100%",
                        height: "auto",
                        borderRadius: 1,
                        aspectRatio: "16/10",
                        objectFit: "cover",
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 0.8,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Variants Section - Show if there are variants */}
      {recipe.variants && recipe.variants.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Card>
            <CardContent>
              <Typography 
                variant="h5"
                className="rotation-plan-subheader"
                sx={{
                  mb: 2,
                  borderBottom: '2px solid var(--color-border-vintage)',
                  pb: 1,
                }}
              >
                Variants
              </Typography>
              <List>
                {recipe.variants.map((variant, index) => (
                  <React.Fragment key={variant.recipe_uuid}>
                    <ListItem
                      component={Link}
                      to={getRecipeUrl(variant)}
                      sx={{ cursor: "pointer", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                    >
                      <ListItemText
                        primary={variant.name}
                        secondary={variant.description || ""}
                      />
                    </ListItem>
                    {index < recipe.variants.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Recipe Form Modal */}
      {recipeFormOpen && (
        <RecipeForm
          open={recipeFormOpen}
          onClose={handleRecipeFormClose}
          recipe={recipe}
          parentRecipeUuid={creatingVariant && recipe ? recipe.recipe_uuid : null}
          onDelete={handleRecipeDelete}
        />
      )}

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent>
          {previewImageUrl && (
            <Box
              component="img"
              src={previewImageUrl}
              alt="Recipe image"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 1,
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Step Edit/Add Modal */}
      <RecipeStepEditModal
        open={stepEditModalOpen}
        onClose={() => {
          setStepEditModalOpen(false);
          setEditingStep(null);
        }}
        step={editingStep}
        onSave={handleSaveStep}
        onDelete={handleDeleteStep}
        recipe={recipe}
        onRecipeRefresh={fetchRecipe}
        onImageDeleted={handleImageDeleted}
      />
    </Box>
  );
};

export default RecipeDetail;


